import React, { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CompProvider, useComp } from "@/components/compliance/compUtils";
import { CompDashboard } from "@/components/compliance/CompDashboard";
import { CompCalendar } from "@/components/compliance/CompCalendar";
import { CompRegister } from "@/components/compliance/CompRegister";
import { CompNC } from "@/components/compliance/CompNC";
import { CompDocs, CompReports } from "@/components/compliance/CompReports";
import { CompReferentiel } from "@/components/compliance/CompReferentiel";
import { ObligationSheet } from "@/components/compliance/ObligationSheet";

const TABS = [["dash", "Tableau de bord"], ["cal", "Calendrier des contrôles"], ["reg", "Registre des obligations"], ["docs", "Documents et attestations"],
  ["nc", "Non-conformités et actions correctives"], ["rep", "Rapports et exports"], ["ref", "Référentiel"]];

function Notifications({ onOpen }) {
  const [d, setD] = useState({ items: [], unread: 0 });
  const load = () => api.get("/compliance/notifications").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const read = async () => { await api.post("/compliance/notifications/read", {}); load(); };
  return (
    <Popover onOpenChange={(o) => { if (!o && d.unread) read(); }}>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Alertes" data-testid="comp-bell" className="relative rounded-full border border-gray-200 bg-white p-2.5 hover:bg-gray-50">
          <Bell className="h-5 w-5 text-gray-600" />
          {d.unread > 0 && <span data-testid="comp-bell-count" className="absolute -right-1 -top-1 rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">{d.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-96 w-80 overflow-y-auto rounded-2xl p-2" data-testid="comp-notifs">
        {d.items.length === 0 ? <p className="p-3 text-sm text-gray-400">Aucune alerte.</p> : d.items.map((n) => (
          <button type="button" key={n.id} onClick={() => n.obligation_id && onOpen(n.obligation_id)} className={`block w-full rounded-xl p-2 text-left text-sm hover:bg-gray-50 ${n.read ? "" : "bg-amber-50/60"}`}>
            <p className="font-semibold">{n.title}</p><p className="text-xs text-gray-500">{n.message}</p>
          </button>))}
      </PopoverContent>
    </Popover>
  );
}

function Shell() {
  const m = useComp();
  const [tab, setTab] = useState("dash");
  const [open, setOpen] = useState(null);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((t) => t + 1);
  const tabs = TABS.filter(([k]) => !["rep", "ref"].includes(k) || m.is_manager);
  return (
    <div className="space-y-4 pb-24" data-testid="comp-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">Conformité & Contrôles</h1>
          <p className="text-sm text-gray-500">Le calendrier réglementaire du cabinet{!m.is_manager && !m.is_hygiene ? " — vos tâches" : ""}</p></div>
        <Notifications onOpen={setOpen} />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-wrap">
        {tabs.map(([k, l]) => <button type="button" key={k} data-testid={`comp-tab-${k}`} onClick={() => setTab(k)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-[#065F46] text-white" : "bg-white text-gray-600 hover:bg-gray-100"}`}>{l}</button>)}
      </div>
      {tab === "dash" && <CompDashboard onOpen={setOpen} tick={tick} />}
      {tab === "cal" && <CompCalendar onOpen={setOpen} tick={tick} />}
      {tab === "reg" && <CompRegister onOpen={setOpen} tick={tick} refresh={refresh} />}
      {tab === "docs" && <CompDocs tick={tick} />}
      {tab === "nc" && <CompNC tick={tick} refresh={refresh} />}
      {tab === "rep" && m.is_manager && <CompReports />}
      {tab === "ref" && m.is_manager && <CompReferentiel refresh={refresh} />}
      <ObligationSheet id={open} onClose={() => setOpen(null)} onChanged={refresh} />
    </div>
  );
}

export default function Conformite() { return <CompProvider><Shell /></CompProvider>; }
